import React from 'react';
import { Card, Table, Space, Typography, Button, Divider } from 'antd';
import { DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';
import { Invoice } from '../../services/pricingService';

const { Title, Text } = Typography;

interface InvoiceViewerProps {
  invoice: Invoice;
  onExportPDF?: () => void;
  onExportCSV?: () => void;
}

const InvoiceViewer: React.FC<InvoiceViewerProps> = ({
  invoice,
  onExportPDF,
  onExportCSV,
}) => {
  const columns = [
    {
      title: 'Feature',
      dataIndex: 'featureName',
      key: 'featureName',
    },
    {
      title: 'Kullanım',
      dataIndex: 'usageCount',
      key: 'usageCount',
      align: 'right' as const,
    },
    {
      title: 'Temel Kota',
      dataIndex: 'baseQuota',
      key: 'baseQuota',
      align: 'right' as const,
    },
    {
      title: 'Aşım',
      dataIndex: 'overageCount',
      key: 'overageCount',
      align: 'right' as const,
      render: (value: number) => (
        <Text type={value > 0 ? 'danger' : undefined}>
          {value > 0 ? `+${value}` : value}
        </Text>
      ),
    },
    {
      title: 'Temel Fiyat',
      dataIndex: 'basePrice',
      key: 'basePrice',
      align: 'right' as const,
      render: (value: number, record: any) => (
        <Text>{value.toFixed(2)} {record.currency}</Text>
      ),
    },
    {
      title: 'Aşım Ücreti',
      dataIndex: 'overagePrice',
      key: 'overagePrice',
      align: 'right' as const,
      render: (value: number, record: any) => (
        <Text type={value > 0 ? 'warning' : undefined}>
          {value.toFixed(2)} {record.currency}
        </Text>
      ),
    },
    {
      title: 'Toplam',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      align: 'right' as const,
      render: (value: number, record: any) => (
        <Text strong>{value.toFixed(2)} {record.currency}</Text>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space direction="vertical" size="small">
          <Title level={4} style={{ margin: 0 }}>
            Fatura - {invoice.brandName}
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            {new Date(invoice.month).toLocaleDateString('tr-TR', { 
              month: 'long', 
              year: 'numeric' 
            })}
          </Text>
        </Space>
      }
      extra={
        <Space>
          {onExportPDF && (
            <Button
              icon={<FilePdfOutlined />}
              onClick={onExportPDF}
            >
              PDF
            </Button>
          )}
          {onExportCSV && (
            <Button
              icon={<DownloadOutlined />}
              onClick={onExportCSV}
            >
              CSV
            </Button>
          )}
        </Space>
      }
    >
      <Table
        dataSource={invoice.features}
        columns={columns}
        rowKey="featureCode"
        pagination={false}
        size="small"
        summary={() => (
          <Table.Summary>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6}>
                <Text strong>TOPLAM</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                  {invoice.total.toFixed(2)} {invoice.currency}
                </Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />

      <Divider />

      {/* Summary Section */}
      <div
        style={{
          background: '#fafafa',
          padding: 16,
          borderRadius: 8,
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text>Ara Toplam:</Text>
            <Text strong>{invoice.subtotal.toFixed(2)} {invoice.currency}</Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Title level={5} style={{ margin: 0 }}>Genel Toplam:</Title>
            <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
              {invoice.total.toFixed(2)} {invoice.currency}
            </Title>
          </div>
        </Space>
      </div>

      {/* Footer Note */}
      <div style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Not: Bu fatura otomatik olarak oluşturulmuştur. Sorularınız için lütfen destek ekibiyle iletişime geçin.
        </Text>
      </div>
    </Card>
  );
};

export default InvoiceViewer;

